const DB_NAME = "apparify-rider-proof-v1";
const DB_VERSION = 1;
const RECORD_STORE = "pendingProofs";
const KEY_STORE = "deviceKeys";
export const PENDING_PROOF_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Protected proof storage failed"));
  });
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error || new Error("Protected proof storage failed"));
    transaction.onabort = () => reject(transaction.error || new Error("Protected proof storage was aborted"));
  });
}

async function openDatabase() {
  if (!window.indexedDB || !window.crypto?.subtle) {
    throw new Error("Protected proof recovery is unavailable on this device");
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(RECORD_STORE)) {
      database.createObjectStore(RECORD_STORE, { keyPath: "id" });
    }
    if (!database.objectStoreNames.contains(KEY_STORE)) {
      database.createObjectStore(KEY_STORE, { keyPath: "deviceInstanceId" });
    }
  };
  return requestResult(request);
}

export function pendingProofId(binding) {
  return [binding.businessId, binding.runId, binding.attemptId].map(Number).join(":");
}

export function pendingProofBindingMatches(stored, current) {
  return Number(stored?.businessId) === Number(current?.businessId)
    && Number(stored?.runId) === Number(current?.runId)
    && Number(stored?.attemptId) === Number(current?.attemptId)
    && String(stored?.authorityId || "") === String(current?.authorityId || "")
    && Number(stored?.authorityVersion) === Number(current?.authorityVersion)
    && String(stored?.deviceInstanceId || "") === String(current?.deviceInstanceId || "");
}

export function pendingProofExpired(record, now = Date.now()) {
  return !record?.expiresAt || Number(record.expiresAt) <= Number(now);
}

function normalizedBinding(binding) {
  const normalized = {
    businessId: Number(binding?.businessId),
    runId: Number(binding?.runId),
    attemptId: Number(binding?.attemptId),
    authorityId: String(binding?.authorityId || ""),
    authorityVersion: Number(binding?.authorityVersion),
    deviceInstanceId: String(binding?.deviceInstanceId || ""),
  };
  if ([normalized.businessId, normalized.runId, normalized.attemptId, normalized.authorityVersion]
    .some((value) => !Number.isInteger(value) || value < 1)
    || !normalized.authorityId || !normalized.deviceInstanceId) {
    throw new Error("Current run authority is required for protected proof recovery");
  }
  return normalized;
}

function additionalData(binding) {
  return new TextEncoder().encode(JSON.stringify(binding));
}

async function deviceKey(database, deviceInstanceId) {
  let transaction = database.transaction(KEY_STORE, "readonly");
  let row = await requestResult(transaction.objectStore(KEY_STORE).get(deviceInstanceId));
  if (row?.key) return row.key;
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  transaction = database.transaction(KEY_STORE, "readwrite");
  const done = transactionDone(transaction);
  transaction.objectStore(KEY_STORE).put({ deviceInstanceId, key });
  await done;
  return key;
}

export async function savePendingDeliveryProof(file, binding, capturedAt = new Date().toISOString()) {
  const normalized = normalizedBinding(binding);
  const database = await openDatabase();
  try {
    const key = await deviceKey(database, normalized.deviceInstanceId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: additionalData(normalized) },
      key,
      await file.arrayBuffer(),
    );
    const transaction = database.transaction(RECORD_STORE, "readwrite");
    const done = transactionDone(transaction);
    transaction.objectStore(RECORD_STORE).put({
      id: pendingProofId(normalized),
      ...normalized,
      fileName: file.name || "delivery-proof.jpg",
      fileType: file.type || "image/jpeg",
      capturedAt,
      savedAt: Date.now(),
      expiresAt: Date.now() + PENDING_PROOF_MAX_AGE_MS,
      iv,
      ciphertext,
    });
    await done;
  } finally {
    database.close();
  }
}

export async function recoverPendingDeliveryProof(binding) {
  const normalized = normalizedBinding(binding);
  const database = await openDatabase();
  try {
    let transaction = database.transaction(RECORD_STORE, "readonly");
    const row = await requestResult(transaction.objectStore(RECORD_STORE).get(pendingProofId(normalized)));
    if (!row) return null;
    if (pendingProofExpired(row) || !pendingProofBindingMatches(row, normalized)) {
      transaction = database.transaction(RECORD_STORE, "readwrite");
      const done = transactionDone(transaction);
      transaction.objectStore(RECORD_STORE).delete(row.id);
      await done;
      return null;
    }
    const key = await deviceKey(database, normalized.deviceInstanceId);
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: row.iv, additionalData: additionalData(normalized) },
      key,
      row.ciphertext,
    );
    return {
      file: new File([plaintext], row.fileName, { type: row.fileType, lastModified: row.savedAt }),
      capturedAt: row.capturedAt,
    };
  } finally {
    database.close();
  }
}

export async function deletePendingDeliveryProof(binding) {
  const normalized = normalizedBinding(binding);
  const database = await openDatabase();
  try {
    const transaction = database.transaction(RECORD_STORE, "readwrite");
    const done = transactionDone(transaction);
    transaction.objectStore(RECORD_STORE).delete(pendingProofId(normalized));
    await done;
  } finally {
    database.close();
  }
}
